import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Scales,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  ChartLineUp,
  Target,
  Heartbeat,
  Question,
  CaretDown,
  Eye,
  Crosshair,
  Timer,
  ShieldCheck,
  Lightbulb,
  BookOpen,
  ArrowsClockwise,
  Funnel,
  Clock
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const ImproveRiskRewardRatio = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'How to Improve Risk Reward Ratio in Trading | Complete Guide',
      description: 'Learn how to improve your risk reward ratio with better execution, psychology, and structured trade analysis.',
      path: '/improve-risk-reward-ratio',
      lang: 'en',
      alternates: getArticleAlternates('/improve-risk-reward-ratio'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "What is a good risk reward ratio?",
      answer: "A commonly cited benchmark is 1:2 or higher—meaning you risk $1 to potentially make $2. However, 'good' depends entirely on your win rate. A trader with 70% accuracy can be profitable with 1:1. A trader with 40% accuracy needs at least 1:2 or better. The key is ensuring your average reward exceeds your average risk over many trades, accounting for your actual win rate."
    },
    {
      question: "Is a 1:2 risk reward ratio enough?",
      answer: "For most strategies, 1:2 provides a solid buffer. With 1:2 R:R, you only need to win 34% of your trades to break even (before costs). At 50% win rate with 1:2, you're solidly profitable. However, achieving consistent 1:2 exits requires discipline—many traders find it psychologically difficult to hold for 2R targets when 1R profit is already on the table."
    },
    {
      question: "Can you be profitable with a low risk reward ratio?",
      answer: "Yes, but it requires a high win rate. Scalpers often use 1:1 or even 0.5:1 R:R with 70%+ win rates. The math works if consistency is there. The danger is that a few losses can quickly erase many small wins. Low R:R strategies demand exceptional discipline and execution—they leave no room for error."
    },
    {
      question: "How does risk reward ratio affect profitability?",
      answer: "R:R directly determines how many losses you can absorb while staying profitable. With 1:3 R:R, one winner covers three losers. With 1:1 R:R, every loss needs a win to recover. Higher R:R provides more margin for error, reduces pressure to maintain high win rates, and creates more sustainable long-term results—but requires patience to let winners run."
    },
    {
      question: "Should I always aim for high risk reward ratios?",
      answer: "Not necessarily. Forcing unrealistic targets leads to missed exits and winners turning into losers. The best approach is matching R:R to market structure—if a logical target is 1.5R away, taking 1.5R is better than hoping for 3R and watching price reverse. Quality setups naturally offer good R:R; poor setups require forcing it."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Scales size={16} weight="fill" />
            <span>Risk Management</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            How to Improve Your Risk Reward Ratio in Trading
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            Most traders obsess over win rate. They want to be right more often, believing that's 
            the path to profitability. But here's what separates struggling traders from successful 
            ones: <strong className="text-white">profitability depends more on how much you win versus 
            how much you lose</strong>—not just how often you win.
          </p>
        </header>

        {/* Introduction */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            A trader with a 40% win rate can be more profitable than one with 60%—if their 
            <strong className="text-white"> risk reward ratio</strong> is significantly better. This 
            counterintuitive reality explains why so many traders with "good" accuracy still lose 
            money, while others win less often but build accounts consistently.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            Improving your <strong className="text-white">risk reward ratio in trading</strong> isn't 
            about finding magical setups. It's about execution, psychology, and the discipline to 
            let winners reach their potential while cutting losers decisively.
          </p>
        </section>

        {/* Section 1: What Is Risk Reward Ratio */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            What Is Risk Reward Ratio?
          </h2>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">Risk reward ratio (R:R)</strong> compares how much you 
              stand to lose versus how much you stand to gain on a trade. If you risk $100 to 
              potentially make $200, your R:R is 1:2 (or simply "2R").
            </p>
            <p className="text-white/70 leading-relaxed">
              The formula is simple: <span className="text-[#00E676] font-mono">Potential Profit ÷ Potential Loss = R:R</span>
            </p>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              In practice, this means defining your stop loss and target before entering. If 
              your stop is 20 pips away and your target is 60 pips, you have a 1:3 R:R. One 
              winner at this ratio covers three losers and still breaks even.
            </p>
          </div>
          
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              { ratio: "1:1", meaning: "Risk equals reward", verdict: "Needs 50%+ win rate" },
              { ratio: "1:2", meaning: "Reward is 2x risk", verdict: "Needs 34%+ win rate" },
              { ratio: "1:3", meaning: "Reward is 3x risk", verdict: "Needs 25%+ win rate" }
            ].map((item, index) => (
              <div key={index} className="bg-[#121212] border border-white/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#00E676] mb-1">{item.ratio}</p>
                <p className="text-white/70 text-sm mb-1">{item.meaning}</p>
                <p className="text-white/50 text-xs">{item.verdict}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: Why Most Traders Struggle */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">02</span>
            Why Most Traders Struggle With Risk Reward
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Understanding R:R mathematically is easy. Executing it consistently is where 
              traders fail. The gap between knowing and doing is filled with emotional interference.
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                title: "Cutting Winners Early",
                description: "The moment a trade shows profit, anxiety kicks in. 'What if it reverses?' So you close at 0.5R instead of letting it reach 2R. Over time, this destroys your average R:R.",
                icon: Timer
              },
              {
                title: "Letting Losses Run",
                description: "When a trade goes against you, hope takes over. 'Maybe it'll come back.' You move your stop or remove it entirely. Small losses become account-damaging losses.",
                icon: Warning
              },
              {
                title: "Emotional Decision Making",
                description: "Fear after a loss leads to taking profit too quickly on the next trade. Greed after a win leads to holding too long. Emotions override your plan.",
                icon: Heartbeat
              },
              {
                title: "Lack of Structure",
                description: "Without predefined targets and stops, every exit becomes a real-time decision under pressure. Pressure leads to poor choices.",
                icon: Crosshair
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: The Psychological Side */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-purple-400 text-lg font-mono">03</span>
            The Psychological Side of Risk Reward
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Trading risk management</strong> isn't just about 
              numbers—it's about managing yourself. The same trader who knows they should hold for 
              2R will close at 0.8R because of psychological pressure. Why?
            </p>
          </div>
          
          <div className="bg-[#121212] border border-purple-500/20 rounded-lg p-8 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-purple-400">
              <Brain size={20} weight="duotone" />
              Key Psychological Barriers
            </h3>
            <div className="space-y-4">
              {[
                {
                  barrier: "Fear of Losing Profits",
                  explanation: "Open profit feels like 'your money' even though the trade isn't closed. The fear of watching it disappear creates premature exits."
                },
                {
                  barrier: "Discomfort Holding Winners",
                  explanation: "Humans are wired to lock in gains and avoid potential loss. Holding a winning trade requires tolerating uncertainty—something most find deeply uncomfortable."
                },
                {
                  barrier: "Impatience",
                  explanation: "Waiting for price to reach your target feels slow. The urge to 'take what the market gives you' overrides the plan you made with a clear head."
                }
              ].map((item, index) => (
                <div key={index} className="border-l-2 border-purple-400/50 pl-4">
                  <h4 className="font-medium text-sm mb-1">{item.barrier}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{item.explanation}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <BookOpen size={24} weight="duotone" className="text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-2">Recommended Reading</h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  <strong className="text-white">"Trading in the Zone"</strong> by Mark Douglas 
                  explores how belief systems and emotional patterns sabotage execution. Douglas 
                  argues that consistent results require thinking in probabilities—accepting that 
                  any single trade's outcome is uncertain, but your edge plays out over many trades. 
                  This mental framework makes it easier to let winners run without emotional interference.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: How to Improve (Practical Steps) */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">04</span>
            How to Improve Risk Reward Ratio (Practical Steps)
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Improving your <strong className="text-white">risk reward ratio trading</strong> results 
              comes from deliberate practice and structural changes—not just willpower. Here's what 
              actually works:
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                step: "1",
                title: "Define Targets Before Entering",
                details: "Never enter a trade without knowing exactly where you'll exit—both profit target and stop loss. Decide when you're calm, not when you're in the heat of price action.",
                icon: Target
              },
              {
                step: "2",
                title: "Set Stop Losses Logically, Not Emotionally",
                details: "Place stops based on market structure (below support, above resistance) rather than arbitrary dollar amounts or what you're 'comfortable' losing. Technical stops respect market behavior.",
                icon: ShieldCheck
              },
              {
                step: "3",
                title: "Let Winners Play Out",
                details: "Use techniques like trailing stops or partial exits to lock in some profit while letting the rest run. This removes the all-or-nothing pressure that leads to premature exits.",
                icon: TrendUp
              },
              {
                step: "4",
                title: "Avoid Impulsive Exits",
                details: "Create rules for when you're allowed to exit early. 'I felt nervous' isn't a valid reason. 'Price broke structure against me' might be. Rules prevent emotional decisions.",
                icon: Crosshair
              },
              {
                step: "5",
                title: "Focus on Execution Consistency",
                details: "Track whether you followed your plan, not just whether you made money. A trade that hit your stop correctly is better execution than a trade where you panicked and exited early for a small gain.",
                icon: ArrowsClockwise
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-[#00E676]/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-full bg-[#00E676]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00E676] font-bold">{item.step}</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <item.icon size={16} className="text-[#00E676]" />
                    {item.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.details}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 5: The Role of Data and Trade Review */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">05</span>
            The Role of Data and Trade Review
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              You can't <strong className="text-white">improve risk reward ratio</strong> if you don't 
              measure it. Most traders have no idea what their actual average R:R is—they remember 
              their best trades and forget the mediocre exits that dragged down their overall performance.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8 mb-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Eye size={20} weight="duotone" className="text-[#00E676]" />
              What to Track
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { metric: "Planned R:R", desc: "What was your intended ratio at entry?" },
                { metric: "Actual R:R", desc: "What ratio did you actually achieve?" },
                { metric: "Exit Reason", desc: "Did you hit target, stop, or exit manually?" },
                { metric: "R:R by Setup", desc: "Which setups produce better ratios?" }
              ].map((item, index) => (
                <div key={index} className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-sm mb-1 text-[#00E676]">{item.metric}</h4>
                  <p className="text-white/50 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              When you track this data, patterns emerge. Maybe your breakout trades average 1.8R 
              but your reversal trades only hit 0.9R. Maybe you consistently exit early on 
              Tuesday afternoons. Without data, these patterns remain invisible. Learning 
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline"> how to analyze your trades</Link> properly 
              is the foundation of improvement.
            </p>
          </div>
        </section>

        {/* Section 6: Why R:R Alone Is Not Enough */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">06</span>
            Why Risk Reward Alone Is Not Enough
          </h2>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed">
              Chasing high R:R without context is a common trap. A trader might set 1:5 targets 
              on every trade, but if they only hit those targets 10% of the time, they're losing 
              money. <strong className="text-white">Risk reward ratio must work together with win rate.</strong>
            </p>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              The relationship between R:R and win rate determines your expectancy—the average 
              amount you expect to make per trade over time. High R:R with low win rate can be 
              profitable. Low R:R with high win rate can also work. But low R:R with low win rate 
              is a guaranteed path to zero.
            </p>
          </div>
          
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-[#121212]">
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Win Rate</th>
                  <th className="text-left py-3 px-4 text-white/60 font-medium">R:R Needed to Break Even</th>
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Verdict</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  { winRate: "30%", rr: "1:2.3+", verdict: "High R:R essential" },
                  { winRate: "40%", rr: "1:1.5+", verdict: "Moderate R:R needed" },
                  { winRate: "50%", rr: "1:1", verdict: "Break-even point" },
                  { winRate: "60%", rr: "1:0.67", verdict: "More flexibility" },
                  { winRate: "70%", rr: "1:0.43", verdict: "Low R:R viable" }
                ].map((row, index) => (
                  <tr key={index} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4 font-medium">{row.winRate}</td>
                    <td className="py-3 px-4 text-[#00E676]">{row.rr}</td>
                    <td className="py-3 px-4 text-white/60">{row.verdict}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Market conditions also matter. Trending markets naturally offer better R:R because 
              price travels further. Ranging markets compress R:R because price reverses more 
              frequently. Forcing the same R:R targets in all conditions leads to frustration.
            </p>
          </div>
        </section>

        {/* Section 7: Improving R:R Through Better Setups */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">07</span>
            Improving Risk Reward Through Better Setups
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Sometimes the best way to <strong className="text-white">improve risk reward ratio</strong> isn't 
              changing how you manage trades—it's being more selective about which trades you take 
              in the first place.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Funnel size={18} className="text-[#00E676]" />
                Higher-Quality Entries
              </h3>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Wait for confluence (multiple factors aligning)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Enter near key levels for tighter stops
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Trade with the trend, not against it
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Skip trades that don't offer acceptable R:R
                </li>
              </ul>
            </div>
            
            <div className="bg-[#121212] border border-red-500/20 rounded-lg p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-red-400">
                <Warning size={18} />
                Avoid Forcing Trades
              </h3>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
                  Don't widen targets to "make" R:R work
                </li>
                <li className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
                  Don't tighten stops beyond logic
                </li>
                <li className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
                  Accept that some setups aren't worth taking
                </li>
                <li className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
                  FOMO trades rarely have good R:R
                </li>
              </ul>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Learning <Link to="/how-to-find-a-trading-setup" className="text-[#00E676] hover:underline">how to find a profitable trading setup</Link> means 
              understanding which patterns naturally offer favorable R:R and which ones require 
              forcing unrealistic expectations.
            </p>
          </div>
        </section>

        {/* Section 8: The Long-Term View */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            The Long-Term View
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Individual trades don't matter as much as traders think. What matters is your 
              average R:R and win rate across hundreds of trades. One losing trade means nothing 
              if your system has positive expectancy. One winning trade means nothing if you 
              can't repeat it consistently.
            </p>
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Building discipline takes time.</strong> You won't 
              suddenly stop closing winners early after reading this article. Improvement happens 
              through deliberate practice—tracking each trade, reviewing your exits, identifying 
              patterns, and gradually rewiring your responses.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-purple-500/10 border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Lightbulb size={22} weight="duotone" className="text-[#00E676]" />
              Key Principles for Long-Term R:R Improvement
            </h3>
            <div className="space-y-3">
              {[
                "Consistency over time matters more than individual trade outcomes",
                "Process focus beats outcome focus for sustainable improvement",
                "Small improvements compound—1% better execution weekly adds up",
                "Your worst trades teach you more than your best trades",
                "Emotional discipline is a skill that strengthens with practice"
              ].map((principle, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle size={18} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 leading-relaxed">{principle}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* References Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <BookOpen size={20} weight="duotone" className="text-white/60" />
            References & Further Reading
          </h2>
          
          <div className="space-y-4">
            <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
              <h4 className="font-medium mb-2">"Trading in the Zone" — Mark Douglas</h4>
              <p className="text-white/60 text-sm leading-relaxed">
                The foundational text on trading psychology. Douglas explains why traders 
                sabotage themselves and how to develop the mental framework for consistent 
                execution. Essential reading for anyone struggling with emotional interference 
                in their trading.
              </p>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
              <h4 className="font-medium mb-2">Behavioral Finance Principles</h4>
              <p className="text-white/60 text-sm leading-relaxed">
                Academic research in behavioral economics (Kahneman, Tversky) demonstrates 
                that humans are inherently loss-averse—we feel losses roughly 2x more intensely 
                than equivalent gains. This explains why letting winners run and cutting losers 
                quickly feels so unnatural, even when we know it's mathematically optimal.
              </p>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Final Thoughts</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Improving your <strong className="text-white">risk reward ratio</strong> comes from 
              better decisions, consistency, and structured review over time. There's no shortcut, 
              no magic indicator, no secret technique. It's the accumulation of hundreds of 
              small improvements in execution and emotional management.
            </p>
            <p className="text-white/70 leading-relaxed">
              Start by tracking your actual R:R—not what you planned, but what you achieved. 
              The gap between those numbers is where your improvement lies. Close that gap 
              systematically, and your results will follow.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Articles:</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades →
              </Link>
              <Link to="/how-to-find-a-trading-setup" className="text-[#00E676] hover:underline text-sm">
                How to Find a Trading Setup →
              </Link>
              <Link to="/manual-vs-automated-trading-journal" className="text-[#00E676] hover:underline text-sm">
                Manual vs Automated Journals →
              </Link>
              <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline text-sm">
                Why Traders Fail to Improve →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to reviewing trades like a pro.</p>
            </Link>
            <Link to="/how-to-find-a-trading-setup" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Find a Trading Setup</h3>
              <p className="text-white/50 text-sm">Discover what makes a profitable trading setup.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck.</p>
            </Link>
            <Link to="/automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">What Is an Automated Journal?</h3>
              <p className="text-white/50 text-sm">Learn why automation beats manual tracking.</p>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Soft CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Scales size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Track Your Risk Reward Over Time
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Improvement starts with measurement. Understanding your actual R:R performance—not 
              what you think it is—reveals exactly where to focus your improvement efforts.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "How to Improve Your Risk Reward Ratio in Trading",
              "description": "Learn how to improve your risk reward ratio with better execution, psychology, and structured trade analysis.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/improve-risk-reward-ratio"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ImproveRiskRewardRatio;
